import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import client from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  Heart, LogOut, RefreshCw,
  TrendingUp, Receipt, Users, CreditCard, Crown, MessageSquare, Tag,
  Mail, FileText, XCircle, Database,
} from "lucide-react";
import { toast } from "sonner";
import EmailDiagnosticsModal from "../components/admin/EmailDiagnosticsModal";
import InvoicesTableSection from "../components/admin/InvoicesTableSection";
import OrphanPaymentsSection from "../components/admin/OrphanPaymentsSection";
import PaymentsHealthSection from "../components/admin/PaymentsHealthSection";
import PendingEuPagoSection from "../components/admin/PendingEuPagoSection";
import WebhookDiagSection from "../components/admin/WebhookDiagSection";
import MoloniStatusSection from "../components/admin/MoloniStatusSection";

const fmtMoney = (n, c = "EUR") =>
  new Intl.NumberFormat("pt-PT", { style: "currency", currency: c }).format(Number(n || 0));
const fmtDate = (s) =>
  s ? new Date(s).toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const fmtMonth = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  const date = new Date(Number(y), Number(m) - 1, 1);
  return date.toLocaleDateString("pt-PT", { month: "short", year: "2-digit" }).replace(".", "");
};
const pkgLabel = (pkg_id) => {
  switch (pkg_id) {
    case "annual":        return "Subscrição Anual";
    case "monthly":       return "Subscrição Mensal";
    case "one_off_year":  return "1 ano";
    case "one_off_month": return "1 mês";
    default:              return pkg_id || "—";
  }
};

const KpiCard = ({ icon: Icon, label, value, sub }) => (
  <div className="bg-[#161920] border border-white/5 rounded-2xl p-6" data-testid={`kpi-${label.replace(/\s+/g, "-").toLowerCase()}`}>
    <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-[#A29F9B] mb-2">
      <Icon className="w-3.5 h-3.5 text-[#C5A880]" /> {label}
    </div>
    <div className="font-display text-3xl text-[#F5F2EE]">{value}</div>
    {sub && <div className="text-xs text-[#A29F9B] mt-2">{sub}</div>}
  </div>
);

const Admin = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelReasons, setCancelReasons] = useState(null);
  const [cancelWindow, setCancelWindow] = useState(90);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [emailDiagOpen, setEmailDiagOpen] = useState(false);
  const [backingUp, setBackingUp] = useState(false);

  // On-demand mongodump → browser download. Doesn't touch local state, just
  // hits the admin-only endpoint and lets the browser save the file. We pass
  // through `client` (axios) with responseType=blob so withCredentials/Bearer
  // are attached the same way as every other admin call.
  const runDbBackup = async () => {
    if (backingUp) return;
    setBackingUp(true);
    const toastId = toast.loading("A gerar backup da base de dados...");
    try {
      const res = await client.post("/admin/db/export-json", null, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/.exec(cd);
      const filename = m ? m[1] : `soulmatch-backup-${new Date().toISOString().slice(0, 10)}.archive.gz`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Backup transferido (${(res.data.size / 1024 / 1024).toFixed(2)} MB)`, { id: toastId });
    } catch (err) {
      // When responseType=blob, axios wraps even JSON error bodies in a Blob.
      // Read it back as text so we can surface the real backend detail
      // (e.g. "mongodump não está disponível neste container.").
      let detail = err?.message || "Falha ao gerar backup.";
      const blob = err?.response?.data;
      if (blob instanceof Blob) {
        try {
          const text = await blob.text();
          try { detail = JSON.parse(text)?.detail || text || detail; }
          catch { detail = text || detail; }
        } catch { /* fall through to err.message */ }
      } else if (typeof blob === "object" && blob?.detail) {
        detail = blob.detail;
      }
      toast.error(detail, { id: toastId });
    } finally { setBackingUp(false); }
  };
  const [emailDiag, setEmailDiag] = useState(null);
  const [emailDiagLoading, setEmailDiagLoading] = useState(false);
  const [emailTestTo, setEmailTestTo] = useState("");
  const [emailTestResult, setEmailTestResult] = useState(null);

  // Invoice filters
  const [filters, setFilters] = useState({
    q: "", package_id: "", date_from: "", date_to: "",
    amount_min: "", amount_max: "", page: 1, page_size: 25,
  });
  const [invoicesRes, setInvoicesRes] = useState({ rows: [], total: 0, page: 1, page_size: 25, revenue_filtered: 0 });
  const [invoicesLoading, setInvoicesLoading] = useState(false);

  const fetchHeader = useCallback(async () => {
    try {
      const [s, t] = await Promise.all([
        client.get("/admin/stats"),
        client.get("/admin/revenue-timeseries?months=12"),
      ]);
      setStats(s.data);
      setSeries(t.data.series || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao carregar dados.");
    } finally { setLoading(false); }
  }, []);

  const fetchInvoices = useCallback(async () => {
    setInvoicesLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) params[k] = v;
      });
      const r = await client.get("/admin/invoices", { params });
      setInvoicesRes(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao carregar faturas.");
    } finally { setInvoicesLoading(false); }
  }, [filters]);

  const fetchCancelReasons = useCallback(async () => {
    setCancelLoading(true);
    try {
      const r = await client.get(`/admin/cancellation-reasons?days=${cancelWindow}`);
      setCancelReasons(r.data);
    } catch (err) {
      console.warn("[admin] cancellation-reasons failed", err);
    } finally { setCancelLoading(false); }
  }, [cancelWindow]);

  useEffect(() => { fetchHeader(); }, [fetchHeader]);
  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);
  useEffect(() => { fetchCancelReasons(); }, [fetchCancelReasons]);

  const onLogout = () => { logout(); navigate("/login"); };
  const setF = (patch) => setFilters((f) => ({ ...f, ...patch, page: patch.page ?? 1 }));
  const totalPages = useMemo(() => Math.max(1, Math.ceil(invoicesRes.total / invoicesRes.page_size)), [invoicesRes]);

  const downloadPdf = async (inv, kind) => {
    // v68+ Fatura-Recibo: same URL is stored on both fields.
    const cachedKey =
      kind === "invoice" ? "invoice_pdf_url"
      : kind === "fr" ? "invoice_pdf_url"
      : "receipt_pdf_url";
    let url = inv[cachedKey] || (kind === "receipt" ? inv.pdf_url : null)
              || (kind === "fr" ? (inv.pdf_url || inv.receipt_pdf_url) : null);
    if (!url) {
      try {
        const r = await client.post(`/invoices/${inv.id}/refresh-pdf`, null, { params: { kind } });
        url = r.data.pdf_url;
      } catch (err) {
        const label = kind === "invoice" ? "Fatura"
                    : kind === "fr" ? "Fatura-Recibo"
                    : "Recibo";
        toast.error(err?.response?.data?.detail || `Falha a obter ${label}.`);
        return;
      }
    }
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  const reissueInvoice = async (inv) => {
    const hasExisting = !!inv.moloni_invoice_id;
    const msg = hasExisting
      ? `A fatura ${inv.invoice_number} já está emitida no Moloni. Emitir uma NOVA (força)? O documento antigo fica no histórico.`
      : `Reemitir fatura+recibo para esta transação (${inv.session_id?.slice(0,18)}...)?`;
    if (!window.confirm(msg)) return;
    try {
      const r = await client.post(
        `/admin/invoices/${encodeURIComponent(inv.session_id)}/reissue`,
        null,
        { params: hasExisting ? { force: true } : {} },
      );
      if (r.data?.status === "already_issued") {
        toast.message(r.data?.hint || "Fatura já emitida.");
      } else {
        toast.success("Reemissão agendada. Atualiza em 5-10s.");
      }
      setTimeout(fetchInvoices, 8000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao reemitir.");
    }
  };

  const openEmailDiag = async () => {
    setEmailDiagOpen(true);
    setEmailDiagLoading(true);
    setEmailTestResult(null);
    try {
      const r = await client.get("/admin/email-diag?limit=10");
      setEmailDiag(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao carregar diagnóstico.");
    } finally {
      setEmailDiagLoading(false);
    }
  };

  const sendTestEmail = async () => {
    if (!emailTestTo || !emailTestTo.includes("@")) {
      toast.error("Indica um email válido.");
      return;
    }
    setEmailDiagLoading(true);
    setEmailTestResult(null);
    try {
      const r = await client.post(
        `/admin/email-diag/test?to=${encodeURIComponent(emailTestTo.trim())}`
      );
      setEmailTestResult(r.data);
      if (r.data?.ok) {
        toast.success("Email de teste enviado.");
      } else {
        toast.error("SMTP não conseguiu entregar — vê o erro abaixo.");
      }
      // Refresh recent_sends list.
      const r2 = await client.get("/admin/email-diag?limit=10");
      setEmailDiag(r2.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha no teste.");
    } finally {
      setEmailDiagLoading(false);
    }
  };

  const forcePasswordReset = async () => {
    const target = emailTestTo.trim();
    if (!target || !target.includes("@")) {
      toast.error("Indica o email do utilizador.");
      return;
    }
    if (!window.confirm(
      `Gerar novo link de recuperação de password para ${target}?\n\n` +
      `O utilizador receberá um email com um link válido 60 minutos.`
    )) return;
    setEmailDiagLoading(true);
    setEmailTestResult(null);
    try {
      const r = await client.post(
        `/admin/force-password-reset?email=${encodeURIComponent(target)}`
      );
      setEmailTestResult({
        ...r.data,
        label: r.data.ok
          ? `Email de recuperação enviado para ${r.data.email} (token 60 min).`
          : `Falhou o envio para ${r.data.email}.`,
      });
      if (r.data?.ok) toast.success("Link de recuperação enviado.");
      else toast.error("SMTP não conseguiu enviar — vê o erro abaixo.");
      const r2 = await client.get("/admin/email-diag?limit=10");
      setEmailDiag(r2.data);
    } catch (err) {
      setEmailTestResult({ ok: false, detail: err?.response?.data?.detail });
      toast.error(err?.response?.data?.detail || "Falha ao forçar recuperação.");
    } finally {
      setEmailDiagLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0C0E12] text-[#F5F2EE]" data-testid="admin-page">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-[#0C0E12]/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-[#C5A880]" />
            <span className="font-display text-xl">Soulmatch</span>
            <span className="ml-2 px-2 py-0.5 rounded-full bg-[#C5A880]/15 border border-[#C5A880]/30 text-[#C5A880] text-[10px] uppercase tracking-wider">
              Admin
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-sm text-[#A29F9B]">{user?.email}</span>
            <button onClick={onLogout} data-testid="admin-logout"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm text-[#A29F9B] hover:text-[#F5F2EE] hover:bg-white/5 transition-colors">
              <LogOut className="w-3.5 h-3.5" /> Sair
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-10 space-y-10">
        <div>
          <span className="label-uppercase">Painel de gestão</span>
          <h1 className="font-display text-3xl md:text-5xl mt-3">Receitas & Faturação</h1>
          <p className="text-[#A29F9B] mt-3 max-w-2xl">
            Visão completa de receitas, subscrições activas e faturas-recibo emitidas pela Solfut, Lda.
          </p>
        </div>

        {/* KPIs */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard icon={TrendingUp} label="Receita total"
            value={stats ? fmtMoney(stats.revenue_total, stats.currency) : "—"}
            sub={stats ? `${stats.invoices_count} fatura${stats.invoices_count === 1 ? "" : "s"} emitida${stats.invoices_count === 1 ? "" : "s"}` : ""} />
          <KpiCard icon={Receipt} label="Últimos 30 dias"
            value={stats ? fmtMoney(stats.revenue_30d, stats.currency) : "—"}
            sub={stats ? `${stats.invoices_30d} fatura${stats.invoices_30d === 1 ? "" : "s"} no período` : ""} />
          <KpiCard icon={CreditCard} label="MRR"
            value={stats ? fmtMoney(stats.mrr, stats.currency) : "—"}
            sub={stats ? `${stats.active_subscriptions} subscrição${stats.active_subscriptions === 1 ? "" : "ões"} activa${stats.active_subscriptions === 1 ? "" : "s"}` : ""} />
          <KpiCard icon={Users} label="Utilizadores"
            value={stats ? stats.users_total.toLocaleString("pt-PT") : "—"}
            sub={stats ? `${stats.users_onboarded} com questionário concluído` : ""} />
        </section>

        <section className="flex flex-wrap gap-3">
          <Link to="/admin/utilizadores" data-testid="admin-users-link"
            className="inline-flex items-center gap-2 bg-[#161920] hover:bg-[#1B1F27] border border-white/5 hover:border-[#C5A880]/40 rounded-xl px-5 py-3 text-sm transition-colors">
            <Users className="w-4 h-4 text-[#C5A880]" />
            Gestão de utilizadores
          </Link>
          <Link to="/admin/cupoes" data-testid="admin-coupons-link"
            className="inline-flex items-center gap-2 bg-[#161920] hover:bg-[#1B1F27] border border-white/5 hover:border-[#C5A880]/40 rounded-xl px-5 py-3 text-sm transition-colors">
            <Tag className="w-4 h-4 text-[#C5A880]" />
            Cupões de desconto
          </Link>
          <Link to="/admin/artigos" data-testid="admin-articles-link"
            className="inline-flex items-center gap-2 bg-[#161920] hover:bg-[#1B1F27] border border-white/5 hover:border-[#C5A880]/40 rounded-xl px-5 py-3 text-sm transition-colors">
            <FileText className="w-4 h-4 text-[#C5A880]" />
            Artigos editoriais
          </Link>
          <button onClick={openEmailDiag} data-testid="admin-email-diag-link"
            className="inline-flex items-center gap-2 bg-[#161920] hover:bg-[#1B1F27] border border-white/5 hover:border-[#C5A880]/40 rounded-xl px-5 py-3 text-sm transition-colors">
            <Mail className="w-4 h-4 text-[#C5A880]" />
            Diagnóstico SMTP
          </button>
          <button onClick={runDbBackup} disabled={backingUp} data-testid="admin-db-backup"
            className="inline-flex items-center gap-2 bg-[#161920] hover:bg-[#1B1F27] border border-white/5 hover:border-[#C5A880]/40 rounded-xl px-5 py-3 text-sm transition-colors disabled:opacity-50">
            <Database className="w-4 h-4 text-[#C5A880]" />
            {backingUp ? "A gerar backup..." : "Backup da BD"}
          </button>
        </section>

        {/* Revenue chart */}
        <section className="bg-[#161920] border border-white/5 rounded-2xl p-6 md:p-8" data-testid="revenue-chart">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
            <div>
              <span className="label-uppercase">Evolução mensal</span>
              <h2 className="font-display text-2xl md:text-3xl mt-2">Receita por mês — últimos 12 meses</h2>
            </div>
            <button onClick={fetchHeader} disabled={loading}
              className="inline-flex items-center gap-1.5 text-sm text-[#A29F9B] hover:text-[#C5A880] disabled:opacity-50">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Atualizar
            </button>
          </div>
          {series.length > 0 ? (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={series} margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
                  <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                  <XAxis dataKey="month" tickFormatter={fmtMonth}
                    tick={{ fill: "#A29F9B", fontSize: 12 }} axisLine={{ stroke: "rgba(255,255,255,0.1)" }} tickLine={false} />
                  <YAxis tickFormatter={(v) => `${v}€`}
                    tick={{ fill: "#A29F9B", fontSize: 12 }} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{ background: "#0C0E12", border: "1px solid rgba(197,168,128,0.3)", borderRadius: 12, color: "#F5F2EE" }}
                    labelFormatter={fmtMonth}
                    formatter={(v, name) => [name === "revenue" ? fmtMoney(v) : v, name === "revenue" ? "Receita" : "Faturas"]}
                  />
                  <Bar dataKey="revenue" fill="#C5A880" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-[#A29F9B]">Sem dados ainda.</p>
          )}
        </section>

        {/* Cancellation reasons */}
        <section className="bg-[#161920] border border-white/5 rounded-2xl p-6 md:p-8" data-testid="cancellation-reasons">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
            <div>
              <span className="label-uppercase inline-flex items-center gap-2">
                <XCircle className="w-3.5 h-3.5 text-rose-400" /> Churn signals
              </span>
              <h2 className="font-display text-2xl md:text-3xl mt-2">Razões de cancelamento</h2>
              <p className="text-sm text-[#A29F9B] mt-1">
                {cancelReasons?.total_responses ?? 0} resposta{cancelReasons?.total_responses === 1 ? "" : "s"} nos últimos {cancelWindow} dias.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <select value={cancelWindow} onChange={(e) => setCancelWindow(Number(e.target.value))}
                data-testid="cancel-window-select"
                className="bg-[#0C0E12] border border-white/10 rounded-lg px-3 py-1.5 text-sm text-[#F5F2EE] focus:border-[#C5A880]/50 focus:outline-none">
                <option value={30}>30 dias</option>
                <option value={90}>90 dias</option>
                <option value={180}>6 meses</option>
                <option value={365}>1 ano</option>
              </select>
              <button onClick={fetchCancelReasons} disabled={cancelLoading}
                className="inline-flex items-center gap-1.5 text-sm text-[#A29F9B] hover:text-[#C5A880] disabled:opacity-50">
                <RefreshCw className={`w-3.5 h-3.5 ${cancelLoading ? "animate-spin" : ""}`} /> Atualizar
              </button>
            </div>
          </div>

          {cancelReasons && (cancelReasons.total_responses > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                {cancelReasons.reasons.map((r) => {
                  const pct = cancelReasons.total_responses > 0
                    ? Math.round((r.count / cancelReasons.total_responses) * 100) : 0;
                  return (
                    <div key={r.key} className="bg-[#0C0E12] border border-white/5 rounded-xl p-4" data-testid={`reason-${r.key}`}>
                      <div className="flex items-baseline justify-between gap-3 mb-2">
                        <span className="text-sm text-[#F5F2EE]">{r.label}</span>
                        <span className="font-display text-xl text-[#C5A880]">{r.count}</span>
                      </div>
                      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full bg-[#C5A880]" style={{ width: `${pct}%` }} />
                      </div>
                      <p className="text-xs text-[#A29F9B] mt-2">{pct}% do total</p>
                    </div>
                  );
                })}
              </div>

              {cancelReasons.comments?.length > 0 && (
                <div>
                  <h3 className="inline-flex items-center gap-2 text-sm text-[#C5A880] mb-3">
                    <MessageSquare className="w-3.5 h-3.5" /> Comentários recentes
                  </h3>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {cancelReasons.comments.map((c) => (
                      <div key={`${c.user_id}-${c.captured_at}`}
                        className="bg-[#0C0E12] border border-white/5 rounded-lg p-3 text-sm"
                        data-testid={`cancel-comment-${c.user_id}`}>
                        <div className="flex items-center justify-between gap-3 mb-1 text-xs text-[#A29F9B]">
                          <span>{c.name || c.email}</span>
                          <span>{fmtDate(c.captured_at)}</span>
                        </div>
                        <p className="text-[#DCD7D2] leading-relaxed whitespace-pre-wrap">{c.comment}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="text-[#A29F9B] text-sm italic">
              Ninguém cancelou (ou ninguém respondeu à pergunta) no período seleccionado. Parabéns — as subscrições estão a reter.
            </p>
          ))}
        </section>

        {/* Moloni auth circuit-breaker — silent unless bloqueado / mal configurado */}
        <MoloniStatusSection />

        {/* Real-time payments stack health (EuPago pending, reminders, Stripe drift) */}
        <PaymentsHealthSection />

        {/* Live webhook diagnostic — critical to spot secret drift / callback URL misconfig */}
        <WebhookDiagSection />

        {/* EuPago pending — customer paid at bank but webhook never activated Premium */}
        <PendingEuPagoSection onChanged={() => { setF({ page: 1 }); }} />

        {/* Reconciliation: paid txs without a local invoice row */}
        <OrphanPaymentsSection onChanged={() => { setF({ page: 1 }); }} />

        {/* Filters + invoices table */}
        <InvoicesTableSection
          fmtMoney={fmtMoney}
          fmtDate={fmtDate}
          pkgLabel={pkgLabel}
          invoicesRes={invoicesRes}
          invoicesLoading={invoicesLoading}
          filters={filters}
          setF={setF}
          totalPages={totalPages}
          onDownloadPdf={downloadPdf}
          onReissue={reissueInvoice}
        />

        <p className="text-center text-xs text-[#A29F9B]/60 pt-4 inline-flex items-center gap-1.5 justify-center w-full">
          <Crown className="w-3 h-3 text-[#C5A880]" /> Painel restrito · Solfut, Lda
        </p>
      </main>

      {/* Email diagnostics modal */}
      <EmailDiagnosticsModal
        open={emailDiagOpen}
        onClose={() => setEmailDiagOpen(false)}
        diag={emailDiag}
        loading={emailDiagLoading}
        testTo={emailTestTo}
        setTestTo={setEmailTestTo}
        testResult={emailTestResult}
        onSendTest={sendTestEmail}
        onForceReset={forcePasswordReset}
      />
    </div>
  );
};

export default Admin;
